import type React from "react";
import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ApiCallbackParams } from "@similarweb/ai-kit";

import { translate } from "app/filters/i18nFilter";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { allTrackers } from "app/services/track/track";

import { AIAgentBanner } from "./AIAgentBanner";
import { AIAgentFrame } from "./AIAgentFrame";
import type { STREAMING_STATUS } from "./constants";
import type { ISEOAGentApiParams } from "./SEOAgentApiService";
import { aiAgent } from "./SEOAgentApiService";
import type { ISelectedDomain } from "./types";
import { logErrorToSentry, logErrorToTrackers } from "./utils";

export const AIAgent: React.FC = () => {
    const { params } = useCurrentStateAndParams();
    const { country, keyword, webSource } = params;
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword as string);
    const [showAiAgentFrame, setShowAiAgentFrame] = useState(false);
    const [selectedDomain, setSelectedDomain] = useState<ISelectedDomain | null>(null);

    const getApiParams = () => ({
        country: country as string | number,
        keywords: [keyword as string],
        main_domain: selectedDomain?.name,
    });

    const handleOpenFrame = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "SEO Agent/Click Banner/Start SEO Agent");
        setShowAiAgentFrame(true);
    };

    const handleCloseFrame = () => {
        allTrackers.dispatchTrackEvent(
            "button",
            "click",
            `SEO Agent/Close Modal with X - ${showAiAgentFrame ? "form" : "result"} step`,
        );
        setShowAiAgentFrame(false);
        setSelectedDomain(null);
    };

    const getAPICallback = async (apiCallbackParams: ApiCallbackParams): Promise<Response> => {
        const apiParams = getApiParams() as ISEOAGentApiParams;
        //const threadId = await aiAgent.createAIAgentThreadAsync();
        try {
            return await aiAgent.startConversation({ payload: apiParams, apiCallbackParams });
        } catch (error) {
            logErrorToSentry(`SEO Agent fetch error: ${error}`, `Error: ${error}`);
            logErrorToTrackers(`Fetch Error`, `Error: ${error}`);
            throw error;
        }
    };

    const handleClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `SEO Agent/Run`);
    };

    const handleDomainSelect = (domain: ISelectedDomain | null) => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", "SEO Agent/Choose Domain");
        setSelectedDomain(domain);
    };

    const title = translate("ai.agent.seo.header.title");
    const badge = translate("ai.agent.seo.header.badge");
    const actionButtonText = translate("ai.agent.seo.header.actionButton");

    const handleMinimize = (streamingStatus: (typeof STREAMING_STATUS)[keyof typeof STREAMING_STATUS]) => {
        allTrackers.dispatchTrackEvent("Button", "click", `SEO Agent/Minimize Modal - ${streamingStatus} step`);
    };

    const handleRestore = (streamingStatus: (typeof STREAMING_STATUS)[keyof typeof STREAMING_STATUS]) => {
        allTrackers.dispatchTrackEvent("Button", "click", `SEO Agent/Restore Modal - ${streamingStatus} step`);
    };

    return (
        <>
            <AIAgentBanner onActionClick={handleOpenFrame} />

            {showAiAgentFrame ? (
                <AIAgentFrame
                    title={title}
                    badge={badge}
                    actionButtonText={actionButtonText}
                    onActionButtonClick={handleClick}
                    onClose={handleCloseFrame}
                    apiCallback={getAPICallback}
                    isKeywordGroup={isKeywordGroup}
                    keyword={keyword}
                    country={country}
                    webSource={webSource}
                    onDomainSelect={handleDomainSelect}
                    selectedDomain={selectedDomain}
                    onMinimize={handleMinimize}
                    onRestore={handleRestore}
                />
            ) : null}
        </>
    );
};
